import { useState, type FormEvent } from "react";
import { ApiError, forgotPassword } from "./api";
import { Shell } from "./Shell";
import { SubmitButton } from "./ui";
import { ApiErrorDisplay } from "./ErrorDisplay";

/**
 * Sends a password-reset request to the Worker. Shows the same "check
 * your email" message on happy-path submit (server anti-enumerates;
 * UI matches) but surfaces real transport errors — network blip,
 * rate-limit, 5xx — with polished category-specific microcopy so the
 * user knows whether to retry or wait.
 */
export function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);

    const handle = async (e: FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            // Anti-enumeration model for this screen:
            //   • network failures       → fall through to "check your
            //     email" so a connectivity blip doesn't distinguish
            //     "email sent / not sent" from "email doesn't exist"
            //   • rate-limit / 5xx       → these happen BEFORE the email
            //     is looked up; they reveal nothing about the address,
            //     so showing the real error lets the user retry sensibly
            //   • non-ApiError throws    → defensive: treat as success
            if (err instanceof ApiError && err.kind !== "network") {
                setError(err);
            } else {
                setSent(true);
            }
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <Shell title="Check your email">
                <div className="done" role="status">
                    <span className="done-tick" aria-hidden="true">✓</span>
                    If that email matches an account, a reset link is on its way.
                </div>
                <p className="hint">
                    <a href="/login" className="link">
                        Back to sign in
                    </a>
                </p>
            </Shell>
        );
    }

    return (
        <Shell
            title="Forgot password"
            subtitle="Enter the email you sign in with and we'll send you a reset link."
        >
            <form onSubmit={handle} noValidate>
                {error && <ApiErrorDisplay error={error} />}
                <div className="field">
                    <label htmlFor="email" className="label">
                        Email address
                    </label>
                    <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="username"
                        autoFocus
                        required
                        disabled={busy}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input"
                        placeholder="you@company.com"
                    />
                </div>
                <div style={{ marginTop: "1.5rem" }}>
                    <SubmitButton
                        busy={busy}
                        disabled={!email.trim()}
                        label="Send reset link"
                        busyLabel="Sending…"
                    />
                </div>
                <p className="hint">
                    <a href="/login" className="link">
                        Back to sign in
                    </a>
                </p>
            </form>
        </Shell>
    );
}
